import {
  FilterQuery,
  ProjectFields,
  YesObject
} from '@yes.technology/react-toolkit'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { setProjectFields } from 'site/Renderer/components/DataTables/DataTables'
import useTableLayouts from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import { GroupedFieldValues } from 'site/Renderer/components/FilterFields/types'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import useObjectFields from 'site/Renderer/components/ObjectView/hooks/useObjectFields'
import { useSiteState } from 'siteState/shared'
import { usePopulateObjectRelations } from 'yesObject/shared'
import useRelationsTableLayouts from '../useRelationsTableLayouts/useRelationsTableLayouts'
import { useEffect, useMemo, useState } from 'react'
import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'

type UseReportDataProps = {
  enabled: boolean
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  classificationUuid?: string
  reportType: 'list' | 'card'
  yesObject?: YesObject
  layoutClassificationUuid?: string
  concatFields?: ConcatFields
  batchSize?: number
  isShowAllObjectFields?: boolean
}

export default function useReportData({
  enabled,
  relationConfig,
  fieldSettings,
  classificationUuid,
  reportType,
  yesObject,
  layoutClassificationUuid,
  concatFields,
  batchSize = 500,
  isShowAllObjectFields
}: UseReportDataProps) {
  const [fieldValues] = useSiteState<GroupedFieldValues>('field-values')
  const [filterFields] = useSiteState<FilterFieldsResponse>('filter-fields')
  const [filterQuery] = useSiteState<FilterQuery>('filter-query')
  const [filterComposition] = useSiteState<string>('field-composition')
  const [objectclassUuid] = useSiteState<string>('objectclassuuid')
  const [filteractionName] = useSiteState<string>('filteraction-name')

  const objectFields = useObjectFields({
    objectclassUuid,
    classificationUuid,
    fieldSettings,
    enabled
  })

  const [tableLayout] = useTableLayouts({
    layoutClassificationUuid,
    objectFields: isShowAllObjectFields
      ? objectFields.objectclass?.fields
      : undefined
  })

  const projectFields: ProjectFields = useMemo(
    () => setProjectFields(tableLayout, concatFields),
    [tableLayout, concatFields]
  )
  const hasProjectFields =
    layoutClassificationUuid && Object.keys(projectFields).length > 0

  const [allObjects, setAllObjects] = useState<YesObject[]>([])
  const [isLoadingYesObjects, setIsLoadingYesObjects] = useState(false)
  const [isFetchedYesObjects, setIsFetchedYesObjects] = useState(false)
  const [errorYesObjects, setErrorYesObjects] = useState<Error | null>(null)
  const [batchProgress, setBatchProgress] = useState({
    currentBatch: 0,
    isFetching: false
  })

  useEffect(() => {
    if (!enabled) return

    const fetchAllObjects = async () => {
      setIsLoadingYesObjects(true)
      setErrorYesObjects(null)
      setBatchProgress({
        currentBatch: 1,
        isFetching: true
      })

      try {
        let allObjects: YesObject[] = []
        let offset = 0
        let hasMore = true

        while (hasMore) {
          const filterBody = {
            query: filterQuery,
            objectclass: objectclassUuid,
            orderby: [],
            pagination: {
              limit: batchSize,
              offset
            },
            skip_metadata: true,
            ...(hasProjectFields ? { project_fields: projectFields } : {})
          }

          const { objects, pagination } = await fetchYesObjects({
            filterBody,
            showLoading: false
          })

          if (objects && objects.length > 0) {
            allObjects = [...allObjects, ...objects]
          }

          hasMore = pagination?.hasNext || false
          offset += batchSize
          setBatchProgress((prev) => ({
            ...prev,
            currentBatch: prev.currentBatch + 1
          }))
        }

        setAllObjects(allObjects)
        setIsFetchedYesObjects(true)
      } catch (error) {
        setErrorYesObjects(
          error instanceof Error ? error : new Error('Failed to fetch objects')
        )
      } finally {
        setIsLoadingYesObjects(false)
        setBatchProgress({
          currentBatch: 0,
          isFetching: false
        })
      }
    }

    fetchAllObjects()
  }, [
    enabled,
    filterQuery,
    objectclassUuid,
    hasProjectFields,
    projectFields,
    batchSize
  ])

  const yesObjects = yesObject ? [yesObject] : allObjects

  const {
    relationsTableLayouts,
    isLoading: isLoadingRelationsLayouts,
    isFetched: isFetchedRelationsLayouts,
    error: errorRelationsLayout
  } = useRelationsTableLayouts({
    relationConfig,
    enabled: enabled && reportType === 'card' && !!relationConfig
  })

  const {
    data,
    isFetching: isLoadingPopulateObjectRelations,
    isFetched: isFetchedPopulateObjectRelations,
    error: errorPopulateObjectRelations,
    populateObjectRelationsProgress
  } = usePopulateObjectRelations({
    objects: yesObjects,
    relationFields: objectFields.relationFields,
    enabled: enabled && isFetchedYesObjects
  })

  return {
    yesObjects: data,
    requestLoading:
      isLoadingYesObjects ||
      isLoadingRelationsLayouts ||
      isLoadingPopulateObjectRelations ||
      objectFields.isLoading,
    requestFetched:
      isFetchedYesObjects &&
      objectFields.isFetched &&
      (isFetchedRelationsLayouts || reportType !== 'card' || !relationConfig) &&
      isFetchedPopulateObjectRelations,
    error:
      errorYesObjects ||
      errorRelationsLayout ||
      errorPopulateObjectRelations ||
      objectFields.error,
    objectFields,
    fieldValues,
    filterFields,
    filterComposition,
    filteractionName,
    relationsTableLayouts,
    hasRequiredReportData: !!filterQuery,
    batchProgress,
    populateObjectRelationsProgress
  }
}
